import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { UnitList } from '@r6digital/rapidstor-types/react/rtk-query/UnitList';
import { BaseReservation, RapidStorReservationRequest } from '@r6digital/rapidstor-types/types/Reservation';

import { PersonaType } from '@/screens';
import Logger from '@/utils/logging';
import { getDefaultNeededDate } from '@/utils/rapidstor';
import { fadeOut } from '@/utils/ui';

import store from '@/store';
import {
    ReservationData,
    selectReservationData,
    selectUnitData,
    setContactCard,
    setReservationData,
    setUnitData,
    UnitData,
} from '@/store/bookingSlice';

import { FormType } from '@/components/elements/contact-card';

import { useGetPersonasQuery, useGetUnitListByTypeQuery, usePostReservationMutation } from '../../../api/rapidstor';
import { AnalyticsContext } from './AnalyticsProvider';

export interface JourneyContextProps {
    location: Location | null;
    unitData: UnitData | null;
    reservationData: ReservationData | null;
    selectedUnitType: UnitType | undefined;
    selectedDescriptor: Descriptor | undefined;
    selectionTypes: PersonaType[] | undefined;
    persona: string | undefined;
    unitList: UnitList | undefined;
    unitListLoading: boolean | undefined;
    unitListSuccess: boolean | undefined;
    unavailableDescriptorIds: string[];
    descriptorList: Descriptor[];
    toggleContactCard: (
        type: FormType,
        selectedUnitType?: UnitType,
        deal?: string 
    ) => void;
    setSelectedUnitType: (unitType: UnitType) => void;
    setSelectedDescriptor: (descriptor: Descriptor) => void;
    setSelectionTypes: (types: PersonaType[]) => void;
    setPersona: (personaId: string | undefined) => void;
    setUnitData: (unitData: UnitData) => void;
    setReservationData: (reservationData: ReservationData) => void;
    setupSliceData: (reservationData: ReservationData, unitData: UnitData) => void;
    setUnavailableDescriptorIds: (ids: string[]) => void;
    handleMakeReservation: (payload: RapidStorReservationRequest, descriptorId: string) => Promise<BaseReservation | false>;
}

export const JourneyContext = createContext<JourneyContextProps>({
    location: null,
    unitData: null,
    reservationData: null,
    persona: undefined,
    selectionTypes: undefined,
    unitList: undefined,
    unitListLoading: undefined,
    unitListSuccess: undefined,

    selectedUnitType: undefined,
    selectedDescriptor: undefined,
    unavailableDescriptorIds: [],
    descriptorList: [],

    toggleContactCard: () => null,
    setSelectedUnitType: () => null,
    setSelectedDescriptor: () => null,
    setSelectionTypes: () => null,
    setPersona: () => null,
    setUnitData: () => null,
    setReservationData: () => null,
    setupSliceData: () => null,
    setUnavailableDescriptorIds: () => null,
    handleMakeReservation: async () => false,
});

export const JourneyContextProvider = ({
    children,
    location,
}: PropsWithChildren<{ location: Location }>) => {
    const unitData = useSelector(selectUnitData);
    const reservationData = useSelector(selectReservationData);
    const navigate = useNavigate();
    const { firebaseLogEvent } = useContext(AnalyticsContext);

    const [makeReservation] = usePostReservationMutation();
    const [persona, setPersona] = useState<string | undefined>();
    const [selectionTypes, setSelectionTypes] = useState<PersonaType[] | undefined>();
    const [selectedUnitType, setSelectedUnitType] = useState<UnitType | undefined>();
    const [selectedDescriptor, setSelectedDescriptor] = useState<
        Descriptor | undefined
    >();
    const [unavailableDescriptorIds, setUnavailableDescriptorIds] = useState<string[]>([]);

    const {
        data: personas,
        error: personasError,
        isError: didPersonasFail,
        isSuccess: didPersonasSucceed,
    } = useGetPersonasQuery({ locationCode: location.locationCode, corpCode: location.corpCode });

    useEffect(() => {
        if (personasError && didPersonasFail) {
            Logger.warn(
                `[JourneyContextProvider] Personas Fetch Error: ${JSON.stringify(
                    personasError
                )}`,
                location
            );
        }
        if (didPersonasSucceed && personas) {
            setSelectionTypes(personas.filter((persona) => persona.available));
        }
    }, [didPersonasFail, didPersonasSucceed, location, personas, personasError]);

    const {
        data: unitList,
        error: unitListError,
        isError: didUnitListFail,
        isFetching: unitListLoading,
        isSuccess: didUnitListSucceed,
    } = useGetUnitListByTypeQuery({
        locationCodes: [location.locationCode],
        persona: persona,
        unitTypeNames: window.rapidstor_settings.rapidstor_options.unitTypeNames,
        unitTypeRegex: window.rapidstor_settings.rapidstor_options.unitTypeNames ? undefined : window.rapidstor_settings.rapidstor_options.unitTypeRegex,
    });

    if (unitListError && didUnitListFail) {
        Logger.warn(
            `[JourneyContextProvider] UnitList Fetch Error: ${JSON.stringify(
                unitListError
            )}`,
            location
        );
    }
    
    const setUnitDataInStore = (unitData: UnitData) => {
        store.dispatch(setUnitData(unitData));
    }

    const setReservationDataInStore = (reservationData: ReservationData) => {
        store.dispatch(setReservationData(reservationData));
    }

    /**
     * Toggles the contact card.
     * 
     * @param type 
     * @param selectedUnitType 
     * @param deal 
     */
    const toggleContactCard = (
        type: FormType,
        selectedUnitType?: UnitType,
        deal?: string,
    ) => {
        store.dispatch(
            setContactCard({
                animate: 'start',
                type: type,
                unitType: selectedUnitType ?? null,
                isDeal: !!deal || false,
                deal: deal || null,
            })
        );
    };

    /** Setup inital slice data */
    const setupSliceData = (reservationData: ReservationData, unitData: UnitData) => {
        setReservationDataInStore(reservationData);
        setUnitDataInStore(unitData);
    }

    /**
     * Sets the units array as empty for the descriptors that are unavailable.
     * 
     * @param descriptors 
     * 
     * @returns {Descriptor[]} Modified Descriptor List
     */
    const unavailableDescriptorHandler = (descriptors: Descriptor[]): Descriptor[] => {
        return descriptors.map((descriptor) => {
            if (unavailableDescriptorIds.includes(descriptor._id)) {
                return {
                    ...descriptor,
                    unitTypes: descriptor.unitTypes.map((unitType) => ({
                        ...unitType,
                        units: [],
                    })),
                };
            }
            return descriptor;
        });
    }

    /**
     * Make reservation handler.
     * 
     * @param payload 
     * @param descriptorId 
     * 
     * @returns {Promise<BaseReservation | false>}
     */
    const handleMakeReservation = async (payload: RapidStorReservationRequest, descriptorId: string): Promise<BaseReservation | false> => {
        try {
            const formattedPayload = {
                ...payload,
                payload: {
                    ...payload.payload,
                    neededDate: moment(payload.payload.neededDate ?? getDefaultNeededDate(location, moment())).format('YYYY-MM-DD'),
                }
            };
            return await makeReservation(formattedPayload).unwrap();
        } catch (error) {
            fadeOut();
            firebaseLogEvent('unit_unavailable_for_move_in', {});
            setUnavailableDescriptorIds([...unavailableDescriptorIds, descriptorId]);
            setTimeout(() => {
                navigate(
                    '/unit-listing', 
                    { 
                        state: { 
                            unitUnavailableAtMoveIn: true,
                            unavailableDescriptor: descriptorId
                        } 
                    }
                );
            }, 300);
            return false;
        }
    }

    /**
     * Core logic for building the final descriptor list.
     * 
     * @return {Descriptor[]} Descriptor List
     */
    const descriptorList = useMemo((): Descriptor[] => {
        if (!unitList || unitListLoading) {
            return [];
        }

        let descriptors = unitList.descriptors;

        if (location.rapidstor.carouselListing) {
            const descriptorsUseForCarousel = descriptors.filter(
                (descriptor) => descriptor.useForCarousel
            );

            if (descriptorsUseForCarousel.length) {
                descriptors = descriptorsUseForCarousel;
            }
        }

        return unavailableDescriptorHandler(descriptors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.rapidstor.carouselListing, unitList, unitListLoading, unavailableDescriptorIds]);

    return (
        <JourneyContext.Provider
            value={{
                location: location,
                toggleContactCard,
                unitData,
                unitListLoading,
                unitListSuccess: didUnitListSucceed,
                reservationData,
                selectedUnitType,
                selectedDescriptor,
                selectionTypes,
                persona,
                unitList,
                unavailableDescriptorIds,
                descriptorList,
                setPersona,
                setSelectionTypes,
                setSelectedUnitType,
                setSelectedDescriptor,
                setUnitData: setUnitDataInStore,
                setReservationData: setReservationDataInStore,
                setupSliceData,
                setUnavailableDescriptorIds,
                handleMakeReservation,
            }}
        >
            {children}
        </JourneyContext.Provider>
    );
};
